import { PRODUCT_OWNERS } from '@felix/domain';
import { Button, Modal } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Logo } from '../../Logo.js';
import classes from './Settings.module.css';
import { Fact, Facts } from './SettingsLayout.js';

/** Üretici (NR467 Pt C Ch 3 Sec 3 7.1.1): ürünün iki sahibi (ADR 0163). */
export const MANUFACTURER = PRODUCT_OWNERS;

export interface AboutContentProps {
  version: string | null;
  /** Derleme kimliği: commit kısa hash'i. */
  build: string | null;
  /** Ek satırlar (ör. gemide gemi sunucusunun sürümü). */
  extra?: readonly { label: string; value: string }[];
  /** Telif yılı; verilmezse bu yıl. */
  year?: number;
}

/**
 * "About Felixmate" içeriği (M9a): ad, sürüm, derleme kimliği ve üretici [NR467 Pt C Ch 3 Sec
 * 3 7.1.1]. Ürün adı her zaman Felixmate (CLAUDE.md "Yazım kuralları").
 */
export function AboutContent({ version, build, extra = [], year }: AboutContentProps) {
  const { t } = useTranslation();
  return (
    <div className={classes.about}>
      <div className={classes.aboutHead}>
        <Logo size={44} withWordmark={false} />
        <div>
          <div className={classes.aboutName}>Felixmate</div>
          <div className={classes.aboutTagline}>{t('settingsScreen.about.description')}</div>
        </div>
      </div>
      <Facts compact>
        <Fact label={t('settingsScreen.about.version')}>{version ?? '—'}</Fact>
        <Fact label={t('settingsScreen.about.build')}>
          <code className={classes.aboutCode}>{build ?? '—'}</code>
        </Fact>
        {extra.map((row) => (
          <Fact key={row.label} label={row.label}>
            {row.value}
          </Fact>
        ))}
        <Fact label={t('settingsScreen.about.manufacturer')}>{MANUFACTURER}</Fact>
      </Facts>
      <p className={classes.aboutCopyright}>
        {t('settingsScreen.about.copyright', {
          year: year ?? new Date().getFullYear(),
          manufacturer: MANUFACTURER,
        })}
      </p>
    </div>
  );
}

export function AboutModal({
  opened,
  onClose,
  ...content
}: AboutContentProps & { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('settingsScreen.about.title')}
      closeButtonProps={{ 'aria-label': t('settingsScreen.about.close') }}
      centered
    >
      <AboutContent {...content} />
      <div className={classes.aboutActions}>
        <Button variant="default" onClick={onClose}>
          {t('settingsScreen.about.close')}
        </Button>
      </div>
    </Modal>
  );
}
